import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

const options: ChooseLevelDropdownProps['level'][] = ['variants', 'products'];

type ChooseLevelDropdownProps = {
  level: 'variants' | 'products';
  disabled?: boolean;
  setLevel: (level: 'variants' | 'products') => void;
};

const ChooseLevelDropdown: FC<ChooseLevelDropdownProps> = ({
  level,
  disabled,
  setLevel,
}) => {
  const { t } = useTranslation(['shipping', 'common']);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (option: typeof level) => {
    setLevel(option);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const ICON_SIZE = 20;

  return (
    <Stack direction='row' alignItems='center' spacing={1}>
      <List
        component='nav'
        aria-label='choose table filter level'
        sx={{
          'bgcolor': 'grey.100',
          'p': 0,
          'borderRadius': 4,
          'transition': 'all 0.3s',
          'cursor': disabled ? 'not-allowed' : 'initial',
          '&:hover': {
            boxShadow: disabled ? 0 : 4,
          },
        }}
      >
        <ListItemButton
          id='choose-filter-level'
          aria-haspopup='listbox'
          aria-controls='choose-filter-level-menu'
          aria-label='Choose Level'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClickListItem}
          disabled={disabled}
          sx={{
            'py': 0,
            'borderRadius': 4,
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <ListItemText
            disableTypography
            primary={
              <Typography variant='caption' color='text.secondary'>
                Choose Level
              </Typography>
            }
            secondary={
              <Stack direction='row' spacing={2} alignItems='center'>
                <Typography variant='body1'>{level}</Typography>
                {open ? (
                  <ExpandLessIcon
                    sx={{ width: ICON_SIZE, height: ICON_SIZE }}
                  />
                ) : (
                  <ExpandMoreIcon
                    sx={{ width: ICON_SIZE, height: ICON_SIZE }}
                  />
                )}
              </Stack>
            }
          />
        </ListItemButton>
      </List>

      <Menu
        id='choose-filter-level-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'choose-filter-level',
          'role': 'listbox',
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            selected={option === level}
            onClick={() => handleMenuItemClick(option)}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>

      {disabled && (
        <Tooltip
          title={t('shipping:chooseLevelHint')}
          placement='top'
          arrow
          PopperProps={{
            sx: { maxWidth: 200 },
          }}
        >
          <InfoOutlinedIcon width={16} height={16} color='inherit' />
        </Tooltip>
      )}
    </Stack>
  );
};

export default ChooseLevelDropdown;
